import React from 'react';
import { t } from 'i18next';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { routes } from '../../../../utils/constants';
import { ShipmentStatuses } from '../../../../utils/enums';
import { colors, zIndexes } from '../../../../theme/style-helpers';
import {
  WidgetContainer, WidgetHeader, WidgetTitle, WidgetTableContainer,
} from '../css';
import { apiGetOrdersAll, apiCancelOrder } from '../../../../services/orders';
import Table from '../../table';
import AppModal from '../../modal';
import Toolbar from '../Toolbar';
import Loader from '../../loader';
import ResultsFeedback from '../ResultsFeedback';
import DownloadModal from './DownloadModal';
import XIcon from '../../../../assets/icons/x_icon.svg';
import AlertModal from '../../../../components/ReactComponent/Modal/AlertModal';
import { ActionButton } from '../../../../components/ReactComponent/Modal/css';

const ModalBoxStyle = {
  overlay: {
    backgroundColor: colors.BLACK + colors.OPACITY_70_ALPHA_HEX_SUFFIX,
    zIndex: zIndexes.MODAL,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: 'auto',
    maxWidth: '90em',
    backgroundColor: colors.LIGHT_GREY,
    padding: '0px 0px 30px 0px',
    overflowY: 'auto',
    height: 'auto',
    maxHeight: '900px',
  },
};

const CloseContainer = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '15px',
};

const ModalBody = {
  padding: '0px 48px',
};

class ShipmentsWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      stts: 'loading',
      data: [],
      modal: {
        isOpen: false,
        title: '',
        subtitle: '',
        actions: null,
        supportMsg: null,
      },
      isDownloadReportModalOpen: false,
      isDownloadErrorModalOpen: false,
    };
  }

  componentDidMount() {
    this.getShipments();
  }

  getShipments = async () => {
    const {
      itemsToShow, ordering, sorting, limitDate, accounts,
    } = this.props;

    const today = new Date();
    const pastMonth = new Date().setDate(today.getDate() - 30);
    const todayDt = format(today, 'MMMM d, yyyy').split('+')[0];
    const pastMonthDt = format(pastMonth, 'MMMM d, yyyy').split('+')[0];
    const pageSize = (itemsToShow) || 20;
    const orderBy = (ordering) || 'CreatedTime';
    const sortBy = (sorting) || 'desc';
    const limitedFrom = (limitDate) || pastMonthDt;
    const accountNumbers = (accounts) || [];

    const response = await apiGetOrdersAll(accountNumbers, null, null, null, limitedFrom, todayDt, pageSize, 1, orderBy, sortBy);

    if (response.status === 200) {
      if (response.data && response.data.total && response.data.total > 0) {
        const dataObj = response.data.orderItems.filter((item) => item.status !== ShipmentStatuses.OrderCanceled);

        this.setState({ data: dataObj, stts: 'loaded' });
      } else {
        this.setState({ stts: 'no-results' });
      }
    } else {
      this.setState({ stts: 'error' });
    }
  };

  closeModal = (callback) => {
    const { modal } = this.state;
    const updatedModal = { ...modal };
    updatedModal.isOpen = false;

    if (callback) {
      this.setState({ modal: updatedModal }, callback);
    }

    if (!callback) {
      this.setState({ modal: updatedModal });
    }
  };

  handleCancelShipment = (zip, id) => {
    const cancelOrder = () => {
      apiCancelOrder(zip, id).then((res) => {
        if (res.hasReturnedError) {
          const updatedModal = {
            isOpen: true,
            title: t('CancelShipmentWarning'),
            subtitle: null,
            actions: null,
            supportMsg: res.message,
          };
          this.setState({ modal: updatedModal, stts: 'loaded' });
        }

        if (res.message && !res.hasReturnedError) {
          const updatedModal = {
            isOpen: true,
            title: t('CancelShipment'),
            subtitle: res.message,
            actions: null,
            supportMsg: null,
          };
          this.setState({ modal: updatedModal, stts: 'loaded' });
        }
      });
    };

    this.setState({ stts: 'updating' }, () => this.closeModal(cancelOrder));
  };

  askForCancelShipmentConfirmation = (id) => {
    const { data } = this.state;
    const selectedOrder = data.filter((item) => item.orderId === id)[0];
    const shipperZip = selectedOrder.from.postalCode;

    const updatedModal = {
      isOpen: true,
      title: t('CancelShipment'),
      subtitle: t('CancelShipmentMessage'),
      actions: [
        {
          label: t('CancelShipment'), action: () => this.handleCancelShipment(shipperZip, id), type: 'primary', arrow: true,
        },
        {
          label: t('GoBack'), action: () => this.closeModal(), type: 'secondary', arrow: false,
        },
      ],
      supportMsg: null,
    };
    this.setState({ modal: updatedModal });
  };

  confirmModifyOrder = (id) => {
    const updatedModal = {
      isOpen: true,
      title: t('WarningTitle'),
      subtitle: t('ModifyAlert'),
      actions: [
        {
          label: t('Continue'), action: () => this.modifyOrder(id), type: 'primary', arrow: true,
        },
        {
          label: t('GoBack'), action: () => this.closeModal(), type: 'secondary', arrow: false,
        },
      ],
      supportMsg: null,
    };
    this.setState({ modal: updatedModal });
  };

  modifyOrder = (id) => {
    const { data } = this.state;
    const selectedOrder = data.find((item) => item.orderId === Number(id));
    const shipmentNumber = selectedOrder?.shipmentNumber;

    window.open(
      `${routes.createShipment}?recreate=true&update=true&probillNumber=${id}&shipmentNumber=${shipmentNumber}`,
    );

    this.closeModal();
  };

  handleDownloadReportClick = () => {
    this.setState({ isDownloadReportModalOpen: true });
  };

  closeDownloadReportModal = () => {
    this.setState({ isDownloadReportModalOpen: false });
  };

  openDownloadErrorModal = () => {
    this.setState({ isDownloadReportModalOpen: false, isDownloadErrorModalOpen: true });
  };

  closeDownloadErrorModal = () => {
    this.setState({ isDownloadErrorModalOpen: false });
  };

  downloadErrorHeader = () => t('Error');

  downloadErrorContent = () => t('DownloadReportErrorPopup');

  downloadErrorActions = () => (
    <ActionButton className="active" iconRightArrow onClick={this.closeDownloadErrorModal}>
      {t('Okay')}
    </ActionButton>
  );

  manageOrders = () => {
    window.open(`${routes.manageOrders}`);
  };

  createNewOrder = () => {
    window.open(`${routes.createShipment}`);
  };

  render() {
    const {
      stts, data, modal, isDownloadReportModalOpen, isDownloadErrorModalOpen,
    } = this.state;
    const {
      className, isAuthenticated, fields, resolution, scrollbarheight, paddingicons, iconsheight, accounts,
    } = this.props;
    const { createShipmentLink, noResultsText, loadingText } = fields;

    const columns = [
      {
        id: 'Actions',
        header: t('Actions'),
        accessor: 'actions',
        type: 'actions',
        params: {
          options: [
            { id: 'ShipmentDetails' },
            { id: 'ShipmentDetails' },
            { id: 'TrackingDetails' },
            { id: 'DownloadBOL' },
            { id: 'DownloadLabelsSM' },
            { id: 'DownloadLabelsLG' },
            { id: 'DuplicateOrder' },
            { id: 'ModifyOrder', action: this.confirmModifyOrder },
            { id: 'CancelOrder', action: this.askForCancelShipmentConfirmation },
          ],
        },
      },
      {
        id: 'Reference',
        header: t('Shipment'),
        accessor: 'shipmentNumber',
        type: 'normal',
      },
      {
        id: 'Shipper',
        header: t('shipper'),
        accessor: 'from.name',
        type: 'name-and-location',
        params: { obj: 'from' },
      },
      {
        id: 'Consignee',
        header: t('consignee'),
        accessor: 'to.name',
        type: 'name-and-location',
        params: { obj: 'to' },
      },
      {
        id: 'Stts',
        header: t('Status'),
        accessor: 'stts',
        type: 'stts',
        params: { percentage: 'statusPercentage', status: 'status', color: 'statusColor' },
      },
      {
        id: 'Created',
        header: t('Created'),
        accessor: 'createdTime',
        type: 'datetime',
      },
      {
        id: 'EstDelivery',
        header: t('EstDelivery'),
        accessor: 'estimatedDeliveryDate',
        type: 'datetime',
      },
      {
        id: 'Delivered',
        header: t('Delivered'),
        accessor: 'deliveryDate',
        type: 'datetime',
      },
      {
        id: 'Updates',
        header: t('Updates'),
        accessor: 'updates',
        type: 'expandableTracking',
      },
    ];

    const toolbarActions = [
      { label: t('DownloadReport'), action: this.handleDownloadReportClick, icon: 'DownloadIcon' },
      { label: t('ViewAllOrManage'), action: this.manageOrders, icon: 'ViewIcon' },
      { label: t('CreateShipment'), action: this.createNewOrder, icon: 'CreateShipmentIcon' },
    ];

    return (
      <WidgetContainer className={className}>

        <Modal
          isOpen={isDownloadReportModalOpen}
          onRequestClose={this.closeDownloadReportModal}
          style={ModalBoxStyle}
          contentLabel="Download Report Modal"
        >
          <div style={CloseContainer}>
            <img src={XIcon} alt={t('Close')} onClick={this.closeDownloadReportModal} style={{ cursor: 'pointer' }} aria-hidden />
          </div>
          <div style={ModalBody}>
            <DownloadModal
              accountNumbers={accounts}
              closeModal={this.closeDownloadReportModal}
              onError={this.openDownloadErrorModal}
            />
          </div>
        </Modal>

        <AlertModal
          isOpen={isDownloadErrorModalOpen}
          onClose={this.closeDownloadErrorModal}
          header={this.downloadErrorHeader}
          content={this.downloadErrorContent}
          actions={this.downloadErrorActions}
        />

        <AppModal
          isOpen={modal.isOpen}
          handleClose={this.closeModal}
          title={modal.title}
          subtitle={modal.subtitle}
          actions={modal.actions}
          support={modal.supportMsg}
          isAuthenticated={isAuthenticated}
        />

        <WidgetHeader>
          <WidgetTitle>{t('Shipments')}</WidgetTitle>
          <Toolbar options={toolbarActions} resolution={resolution} paddingicons={paddingicons} iconsheight={iconsheight} />
        </WidgetHeader>

        <WidgetTableContainer>

          {(stts === 'loading' || stts === 'updating')
            ? <Loader subtitle={loadingText.value} isLoading />
            : null}

          {(stts === 'loaded')
            ? (
              <Table
                datasource={data}
                columns={columns}
                viewAll={this.manageOrders}
                resolution={resolution}
                isShipments
                scrollbarheight={scrollbarheight}
              />
            )
            : null}
          {(stts === 'no-results')
            ? (
              <ResultsFeedback
                icon="ShipmentTrackingIcon"
                message={noResultsText.value}
                ctaLabel={
                        (createShipmentLink && createShipmentLink.jss && createShipmentLink.jss.value && createShipmentLink.jss.value.text)
                        || t('CreateShipment')
                    }
                ctaAction={this.createNewOrder}
              />
            )
            : null}

        </WidgetTableContainer>

      </WidgetContainer>
    );
  }
}

ShipmentsWidget.propTypes = {
  className: PropTypes.string,
  itemsToShow: PropTypes.number,
  ordering: PropTypes.string,
  sorting: PropTypes.string,
  limitDate: PropTypes.instanceOf(Date),
  accounts: PropTypes.array,
  isAuthenticated: PropTypes.bool,
  fields: PropTypes.object,
  resolution: PropTypes.string,
  scrollbarheight: PropTypes.string,
  paddingicons: PropTypes.string,
  iconsheight: PropTypes.string,
};

export default ShipmentsWidget;
